import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { SetActiveThemeForUser } from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { removeSpaceFromString } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  BaseWidgetProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, map, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-theme-selector-widget',
  templateUrl: './theme-selector-widget.component.html',
})
export class ThemeSelectorWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public widgetConfigData: BaseWidgetProperties;
  public override contentStyle: baseCssConfigStyle;
  public iconSize: baseCssConfigStyle;
  public textContentStyle: baseCssConfigStyle;
  public isChecked: boolean;
  private isWorkflow: boolean;
  private content = '';
  public renderedContent = '';

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private store: Store,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadWidgetConfigurations();
    this.onLoadWidgetContent();
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
  }
  private loadWidgetConfigurations() {
    if (this.dataIn != undefined) {
      this.widgetConfigData = this.dataIn.attri?.widgetProperties;
      this.isChecked = this.setToggleState();
    }
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.COLOR,
    ]);

    this.iconSize = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.ICON_SIZE,
    ]);

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);
  }
  private onLoadWidgetContent(): void {
    this.content = this.dataIn?.attri?.widgetProperties?.textContent ?? '';
    const template = Handlebars.compile(this.content);
    buildHandleBarModel(
      this.store,
      this.content,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private setToggleState(): boolean {
    const toggleStateFromWidgetConfig = this.dataIn.attri?.widgetProperties['toggleDefaultState'] ?? false;
    const activeThemeFromProfile = this.store.selectSnapshot(ProfileState.getProfile)?.activeTheme ?? '';
    const configuredTheme = localStorage.getItem('configuredTheme') ?? '';
    if (configuredTheme?.length && activeThemeFromProfile?.length) {
      const isSameTheme = !!(configuredTheme === activeThemeFromProfile);
      if (isSameTheme) {
        return this.checkThemesAndReturnState(activeThemeFromProfile);
      } else {
        return this.checkThemesAndReturnState(configuredTheme);
      }
    }
    return toggleStateFromWidgetConfig;
  }

  private checkThemesAndReturnState(themeToCheck: string): boolean {
    const isToggleOnTheme = !!(this.dataIn.attri.widgetProperties['toggleOnTheme'] === themeToCheck);
    const isToggleOffTheme = !!(this.dataIn.attri.widgetProperties['toggleOffTheme'] === themeToCheck);
    if (isToggleOnTheme) {
      return true;
    }
    if (isToggleOffTheme) {
      return false;
    }
    return false;
  }

  public onChecked(event: boolean) {
    this.dataIn.attri.widgetProperties['toggleDefaultState'] = event;
    this.isChecked = event;
    const themeName = event
      ? this.dataIn.attri.widgetProperties['toggleOnTheme']
      : this.dataIn.attri.widgetProperties['toggleOffTheme'];
    // to avoid theme change in app builder check config type
    if (!this.isWorkflow) this.changeTheme(themeName);
  }

  private changeTheme(newTheme: string): void {
    const head = this.document.getElementsByTagName('head')[0];
    const originPath = this.getOriginPath();

    // First, add new stylesheet without removing old one
    const newThemeLink = this.renderer.createElement('link');
    this.renderer.setAttribute(newThemeLink, 'rel', 'stylesheet');
    this.renderer.setAttribute(newThemeLink, 'type', 'text/css');
    this.renderer.setAttribute(
      newThemeLink,
      'href',
      `${originPath}/assets/themes/theme-${removeSpaceFromString(newTheme)}.css`,
    );
    this.renderer.setAttribute(newThemeLink, 'id', 'newGlobalTheme');

    // Add onload event listener to the link element
    newThemeLink.onload = () => {
      this.switchTheme('newGlobalTheme');
    };

    // Add new stylesheet
    this.renderer.appendChild(head, newThemeLink);
    this.updateActiveThemeForUser(newTheme);
  }

  private switchTheme(newThemeId: string) {
    const currentGlobalThemeEl = this.document.getElementById('globalTheme');
    const newGlobalThemeEl = this.document.getElementById(newThemeId);

    // Remove the old theme
    if (currentGlobalThemeEl) {
      document.head.removeChild(currentGlobalThemeEl);
    }

    // Update new theme element id
    if (newGlobalThemeEl) {
      newGlobalThemeEl.id = 'globalTheme';
    }
  }

  private getOriginPath(): string {
    let originPathFromConfig = '';
    this.configService
      .getApi()
      .pipe(
        take(1),
        map((path) => {
          originPathFromConfig = path;
        }),
      )
      .subscribe();
    return window.location.origin.includes('localhost') ? originPathFromConfig : window.location.origin;
  }

  private updateActiveThemeForUser(newTheme: string) {
    this.store.dispatch(new SetActiveThemeForUser(newTheme));
  }
}
